import { CATEGORIES, Platform } from "@/types";

export interface ParsedKbFile {
  filename: string;
  title: string;
  category: string;
  platforms: Platform[];
  body: string;
  warnings: string[];
}

const TITLE_KEYS = ["title", "subject", "name"];
const CATEGORY_KEYS = ["category", "topic", "type"];
const PLATFORM_KEYS = ["platforms", "platform", "os", "operating system", "operatingsystem"];

export function parseKbMarkdown(content: string, filename: string): ParsedKbFile {
  const warnings: string[] = [];
  let title = "";
  let category = "";
  let platformsRaw = "";
  let body = content;

  // 1. Try YAML-style frontmatter: ---\nkey: value\n---
  const fmMatch = body.match(/^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?/);
  if (fmMatch) {
    const fm = fmMatch[1];
    const fmTitle = matchAny(fm, TITLE_KEYS);
    const fmCategory = matchAny(fm, CATEGORY_KEYS);
    const fmPlatforms = matchAny(fm, PLATFORM_KEYS);
    if (fmTitle) title = fmTitle;
    if (fmCategory) category = fmCategory;
    if (fmPlatforms) platformsRaw = fmPlatforms;
    body = body.slice(fmMatch[0].length);
  }

  // 2. Try plain `Key: value` lines at the top of the document.
  //    Also handles `**Key:** value`, `__Key:__ value`, and similar
  //    markdown-emphasis wrappers around the key and/or value.
  if (!title || !category || !platformsRaw) {
    const lines = body.split(/\r?\n/);
    const headerLines = lines.slice(0, 16); // a bit more headroom for emphasized headers
    let consumed = 0;
    for (const rawLine of headerLines) {
      const line = stripEmphasis(rawLine);
      const m = line.match(/^([A-Za-z][A-Za-z0-9 _-]*?):\s*(.+)$/);
      if (!m) {
        if (rawLine.trim() === "" || rawLine.startsWith("#")) { consumed += rawLine.length + 1; continue; }
        break;
      }
      const key = m[1].toLowerCase().trim();
      const val = stripQuotes(m[2].trim());
      if (!title && TITLE_KEYS.includes(key)) title = val;
      else if (!category && CATEGORY_KEYS.includes(key)) category = val;
      else if (!platformsRaw && PLATFORM_KEYS.includes(key)) platformsRaw = val;
      else if (key === "body") break; // explicit body marker — stop header parsing here
      else break;
      consumed += rawLine.length + 1;
    }
    if (consumed > 0 && (title || category || platformsRaw)) {
      body = body.slice(consumed);
    }
  }

  // 3. Try first H1 as title fallback
  if (!title) {
    const h1 = body.match(/^#\s+(.+)$/m);
    if (h1) {
      title = h1[1].trim();
      body = body.replace(h1[0], "").trimStart();
    }
  }

  // 4. Filename fallback
  if (!title) {
    title = filename.replace(/\.(md|markdown|txt)$/i, "").replace(/[-_]+/g, " ").trim();
    if (title) warnings.push("Title inferred from filename");
  }

  // 5. Match category to a known one
  if (!category) {
    category = "Other";
    warnings.push("No category found — defaulted to Other");
  } else {
    const matched = matchCategory(category);
    if (matched !== category) {
      warnings.push(`Category "${category}" mapped to "${matched}"`);
      category = matched;
    }
  }

  body = body.trimStart();

  // Parse platforms: split on commas, normalize, drop unknowns
  const platforms = parsePlatforms(platformsRaw);
  if (platformsRaw && platforms.length === 0) {
    warnings.push(`Platforms "${platformsRaw}" didn't match any known OS — left empty (applies to all)`);
  }

  return { filename, title, category, platforms, body, warnings };
}

function parsePlatforms(raw: string): Platform[] {
  if (!raw) return [];
  // Strip YAML array brackets if present
  const inner = raw.replace(/^\[|\]$/g, "");
  const parts = inner.split(/[,;]/).map((s) => stripQuotes(s.trim()));
  const out: Platform[] = [];
  for (const p of parts) {
    const m = matchPlatform(p);
    if (m && !out.includes(m)) out.push(m);
  }
  return out;
}

function matchPlatform(input: string): Platform | null {
  const norm = input.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!norm) return null;
  if (/^mac|^osx|darwin/.test(norm)) return "macos";
  if (/^win/.test(norm)) return "windows";
  if (/^ios|iphone|ipad/.test(norm)) return "ios";
  if (/^and/.test(norm)) return "android";
  if (/^lin|^ubuntu|^debian|^fedora|^centos|^arch/.test(norm)) return "linux";
  if (/^web|browser|chrome|firefox|safari|edge/.test(norm)) return "web";
  if (/^all|^any|^cross/.test(norm)) return null; // means "no filter"
  return null;
}

function matchAny(yaml: string, keys: string[], stopAtKeys: string[] = []): string {
  for (const key of keys) {
    const re = new RegExp(`^${key}\\s*:\\s*(.+)$`, "im");
    const m = yaml.match(re);
    if (m && !stopAtKeys.includes(key)) return stripQuotes(m[1].trim());
  }
  return "";
}

function stripQuotes(s: string): string {
  return s.replace(/^["']|["']$/g, "");
}

// Strip markdown bold/italic/code emphasis from a line so headers like
// `**Title:** value` or `__Category:__ value` parse the same as plain text.
function stripEmphasis(line: string): string {
  return line
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(?<![\\*])\*([^*\n]+)\*/g, "$1")
    .replace(/(?<![\\_])_([^_\n]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim();
}

function matchCategory(input: string): string {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const target = norm(input);
  // Exact normalized match
  for (const cat of CATEGORIES) if (norm(cat) === target) return cat;
  // Substring either direction
  for (const cat of CATEGORIES) {
    const c = norm(cat);
    if (target.includes(c) || c.includes(target)) return cat;
  }
  // Keyword match by category aliases — order matters: more specific first.
  // For "troubleshooting" / "performance" type categories we map to the
  // canonical category that best fits the practical topic of the article.
  const aliases: Record<string, string[]> = {
    "Email / M365":     ["email", "mail", "m365", "exchange", "outlook", "calendar", "365"],
    "Account & Access": ["account", "access", "auth", "password", "login", "signin", "sso", "mfa", "2fa", "permission", "role"],
    "Network & VPN":    ["network", "vpn", "wifi", "wireless", "internet", "connectivity", "ethernet", "dns", "proxy"],
    "Hardware":         ["hardware", "laptop", "computer", "desktop", "monitor", "display", "printer", "device", "battery", "screen", "keyboard", "mouse", "performance", "slow", "speed", "freeze", "crash", "boot", "startup"],
    "Software & Apps":  ["software", "app", "application", "office", "install", "update", "browser", "chrome", "firefox", "edge", "safari", "zoom", "teams", "slack", "antivirus"],
  };
  for (const [cat, keys] of Object.entries(aliases)) {
    if (keys.some((k) => target.includes(k))) return cat;
  }
  return "Other";
}
